import { getClassAvailabilityLabel } from "@/lib/utils/classAvailability";
import { formatAgeNumber } from "@/lib/utils/classPublicDisplay";
import { getCivilDateParts } from "@/lib/utils/date/date-only";
import { parseDecimal } from "@/lib/utils/format-decimal";
import type {
  GetAvailableClassResponse,
  GetClassResponse,
  GetStudentResponse,
} from "@/openapi/generated";
import { Gender, GenderComposition } from "@/openapi/generated";

export function mapClassResponseToAvailableClass(
  cls: GetClassResponse,
): GetAvailableClassResponse {
  return {
    classId: cls.classId,
    name: cls.name,
    slug: cls.slug,
    imageUrl: cls.imageUrl,
    description: cls.description,
    classDays: cls.classDays,
    classStartDate: cls.classStartDate,
    classEndDate: cls.classEndDate,
    classStartTime: cls.classStartTime,
    classEndTime: cls.classEndTime,
    hasWaitList: cls.hasWaitList,
    isContinuous: cls.isContinuous,
    maxNumberOfStudents: cls.maxNumberOfStudents,
    currentNumberOfStudents: cls.currentNumberOfStudents,
    classRoom: cls.classRoom,
    location: cls.location,
  };
}

/** Completed years + months since birth, as of `asOf` (default: now). */
export function calculateStudentAgeInMonths(
  dateOfBirth: Date,
  asOf: Date = new Date(),
): number {
  const birth = getCivilDateParts(dateOfBirth);
  const now = {
    year: asOf.getFullYear(),
    month: asOf.getMonth() + 1,
    day: asOf.getDate(),
  };
  if (!birth) return 0;

  let months = (now.year - birth.year) * 12 + (now.month - birth.month);
  if (now.day < birth.day) months -= 1;
  return Math.max(0, months);
}

/** Age in years including months, e.g. 4 years 6 months → 4.5. */
export function calculateStudentAgeYears(
  dateOfBirth: Date,
  asOf: Date = new Date(),
): number {
  return calculateStudentAgeInMonths(dateOfBirth, asOf) / 12;
}

export function formatStudentAgeLabel(
  dateOfBirth: Date,
  asOf: Date = new Date(),
): string {
  const months = calculateStudentAgeInMonths(dateOfBirth, asOf);
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const yearLabel = years === 1 ? "1 year" : `${years} years`;
  if (remainingMonths === 0) return yearLabel;
  const monthLabel =
    remainingMonths === 1 ? "1 month" : `${remainingMonths} months`;
  return `${yearLabel} ${monthLabel}`;
}

function classAgeYearsToMonths(ageYears: number): number {
  return Math.round(ageYears * 12);
}

export function parseClassAge(value: unknown): number | null {
  return parseDecimal(value);
}

export function getGenderIneligibilityReason(
  studentGender: Gender,
  classGenderComposition: GenderComposition | null | undefined,
): string | null {
  if (
    !classGenderComposition ||
    classGenderComposition === GenderComposition.Coeducation
  ) {
    return null;
  }

  if (
    classGenderComposition === GenderComposition.Male &&
    studentGender !== Gender.Male
  ) {
    return "This class is for male students only.";
  }

  if (
    classGenderComposition === GenderComposition.Female &&
    studentGender !== Gender.Female
  ) {
    return "This class is for female students only.";
  }

  return null;
}

export interface DeepLinkClassUnavailabilityInput {
  highlightClassId: number;
  highlightClass: GetClassResponse | null | undefined;
  student: Pick<
    GetStudentResponse,
    "firstName" | "lastName" | "dateOfBirth" | "gender" | "isActive"
  > | null;
  enrolledClassIds: Set<number>;
  isInAvailableList: boolean;
  hiddenByFilters: boolean;
  seasonFilter: number | null;
}

export interface DeepLinkClassUnavailabilityResult {
  reasons: string[];
  canResetFilters: boolean;
}

export function getDeepLinkClassUnavailability(
  input: DeepLinkClassUnavailabilityInput,
): DeepLinkClassUnavailabilityResult {
  const reasons: string[] = [];
  let canResetFilters = false;

  const studentName = input.student
    ? `${input.student.firstName} ${input.student.lastName}`
    : "This student";

  if (input.student?.isActive === false) {
    return {
      reasons: [`${studentName} is inactive and cannot register for classes.`],
      canResetFilters: false,
    };
  }

  if (input.enrolledClassIds.has(input.highlightClassId)) {
    return {
      reasons: [`${studentName} is already enrolled in this class.`],
      canResetFilters: false,
    };
  }

  if (input.hiddenByFilters) {
    reasons.push(
      "This class is hidden by your current search or filter settings.",
    );
    canResetFilters = true;
    return { reasons, canResetFilters };
  }

  const cls = input.highlightClass;

  if (
    input.seasonFilter != null &&
    cls?.season?.seasonId != null &&
    Number(cls.season.seasonId) !== input.seasonFilter &&
    !input.isInAvailableList
  ) {
    const seasonName = cls.season.name?.trim();
    reasons.push(
      seasonName
        ? `This class belongs to ${seasonName}, which is excluded by your season filter.`
        : "This class is excluded by your selected season filter.",
    );
    canResetFilters = true;
  }

  if (cls?.isActive === false) {
    reasons.push("This class is not currently open for registration.");
  }

  const availability = cls ? getClassAvailabilityLabel(cls) : null;
  if (availability === "Class full") {
    reasons.push("This class is full and does not have a waitlist.");
  }

  if (input.student?.dateOfBirth && cls) {
    const ageMonths = calculateStudentAgeInMonths(input.student.dateOfBirth);
    const minAge = parseClassAge(cls.minAge);
    const maxAge = parseClassAge(cls.maxAge);
    const ageLabel = formatStudentAgeLabel(input.student.dateOfBirth);
    const belowMin =
      minAge != null && ageMonths < classAgeYearsToMonths(minAge);
    const aboveMax =
      maxAge != null && ageMonths > classAgeYearsToMonths(maxAge);

    if (belowMin) {
      reasons.push(
        minAge != null && maxAge != null
          ? `${studentName} is ${ageLabel} old; this class is for ages ${formatAgeNumber(minAge)}–${formatAgeNumber(maxAge)}.`
          : `${studentName} is ${ageLabel} old; this class requires a minimum age of ${formatAgeNumber(minAge) ?? minAge}.`,
      );
    } else if (aboveMax) {
      reasons.push(
        minAge != null && maxAge != null
          ? `${studentName} is ${ageLabel} old; this class is for ages ${formatAgeNumber(minAge)}–${formatAgeNumber(maxAge)}.`
          : `${studentName} is ${ageLabel} old; this class accepts students up to age ${formatAgeNumber(maxAge) ?? maxAge}.`,
      );
    }
  }

  if (input.student?.gender && cls) {
    const genderReason = getGenderIneligibilityReason(
      input.student.gender,
      cls.genderComposition,
    );
    if (genderReason) {
      reasons.push(genderReason);
    }
  }

  if (reasons.length === 0 && !input.isInAvailableList) {
    reasons.push(
      `${studentName} cannot register for this class based on current eligibility requirements.`,
    );
  }

  return { reasons, canResetFilters };
}
